import { ApiError } from '../client';
import { BoxStatusSchema, type BoxStatus } from '../schemas/boxStatus';
import { deliveryDetailMockIds, deliveryDetailMockBoxIds } from './deliveryDetail';

// One case per fixture «buzón», so walking the showcase deliveries covers every banner state without
// touching a toggle. Fictional data only.
const CASE_BY_BOX: Record<string, string> = {
  [deliveryDetailMockBoxIds.storedExtended]: 'free_no_subsequent',
  [deliveryDetailMockBoxIds.retrieved]: 'occupied_by_other',
  [deliveryDetailMockBoxIds.expired]: 'free_with_subsequent',
  [deliveryDetailMockBoxIds.nonCollected]: 'reserved_for_other_no_subsequent',
  [deliveryDetailMockBoxIds.collected]: 'reserved_for_other_with_subsequent',
};

// The `stored` and `blocked` fixtures share box DIZ03, the one place the fixtures can show why the
// read takes both ids: the delivery sitting in it sees «caso propio» (no banner), while the other one
// sees a box held by somebody else. Keying on the box alone would force both to one answer and
// quietly encode a falsehood about the fixtures.
const CASE_BY_SHARED_BOX_DELIVERY: Record<string, string> = {
  [deliveryDetailMockIds.stored]: 'own_delivery',
  [deliveryDetailMockIds.blocked]: 'occupied_by_other',
};

// Dev/test hooks: force any case string (including one the panel does not know, to exercise the
// fail-closed path) or a read failure.
let forcedCase: string | null = null;
let forcedError = false;

export function setBoxStatusMockCase(value: string | null): void {
  forcedCase = value;
}

export function setBoxStatusMockError(fail: boolean): void {
  forcedError = fail;
}

export function resetBoxStatusMock(): void {
  forcedCase = null;
  forcedError = false;
}

export async function fetchBoxStatusMock(boxId: string, deliveryId: string): Promise<BoxStatus> {
  // Simulate network latency so the slot's skeleton is actually visible in dev.
  await new Promise((resolve) => setTimeout(resolve, 100));
  if (forcedError) {
    throw new ApiError(404, 'box_not_found', { code: 'box_not_found' }, 'box status failed');
  }
  // An unlisted box falls back to `never_stored` — a legitimate no-banner outcome, not an error.
  const resolved =
    boxId === deliveryDetailMockBoxIds.stored
      ? CASE_BY_SHARED_BOX_DELIVERY[deliveryId]
      : CASE_BY_BOX[boxId];
  return BoxStatusSchema.parse({ case: forcedCase ?? resolved ?? 'never_stored' });
}
